/**
 * Service de tracking des funnels MyLimni
 *
 * Utilise les événements Tianji existants pour créer des funnels complets :
 * - Funnel Réservation : du mini-site jusqu'au paiement
 * - Funnel Onboarding : de l'inscription à la première réservation client
 * - Funnel Activation : utilisation des fonctionnalités clés
 *
 * Compatible avec l'API native Tianji (window.tianji.track)
 */

import type { App } from "vue";
import { tianjiService } from "./tianji.service";
import { useAuthStore } from "@/stores/authStore";

export interface FunnelStepData {
  funnel: string;
  step: string;
  userId?: string;
  organizationId?: string;
  sessionId?: string;
  lakeId?: string;
  [key: string]: unknown;
}

export interface FunnelConfig {
  name: string;
  steps: string[];
  description: string;
}

// Configuration des funnels basée sur les événements existants
export const FUNNEL_CONFIGS: Record<string, FunnelConfig> = {
  reservation: {
    name: "Parcours de réservation",
    description: "Du mini-site public jusqu'au paiement validé",
    steps: [
      "landing_page_vue", // Arrivée sur mini-site public
      "lake_details_vue", // Vue détail d\'un plan d\'eau
      "reservation_started", // ✅ Existant - Première sélection poste
      "formulaire_renseigne", // Infos client remplies
      "reservation_submitted", // ✅ Existant - Réservation soumise
      "paiement_valide", // Paiement confirmé
    ],
  },

  onboarding: {
    name: "Onboarding gestionnaire",
    description: "De l'inscription à la première réservation client",
    steps: [
      "account_created", // ✅ Existant - Création compte OAuth
      "manager_dashboard_vue", // Première visite dashboard
      "calendar_created", // ✅ Existant - Premier plan d\'eau créé
      "availability_created", // ✅ Existant - Premiers créneaux créés
      "stripe_account_connected", // ✅ Existant - Compte Stripe connecté
      "lake_configuration_guide_shown", // Guide de configuration affiché
      "lake_configuration_guide_completed", // Guide de configuration terminé
      "premiere_reservation_client", // Première réservation d\'un client
    ],
  },

  activation: {
    name: "Activation fonctionnalités",
    description: "Utilisation des fonctionnalités avancées",
    steps: [
      "feature_activated", // ✅ Existant - Première fonctionnalité activée
      "pricing_engine_activated", // Moteur de tarification activé
      "promotions_created", // Première promotion créée
      "reservation_options_used", // Options de réservation utilisées
      "support_clicked", // ✅ Existant - Premier usage du support
      "dashboard_consultation", // Consultation régulière des stats
    ],
  },
};

class FunnelService {
  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
   * Track une étape de funnel avec enrichissement automatique
   */
  async trackStep(data: FunnelStepData): Promise<void> {
    const authStore = useAuthStore();

    const enrichedData: FunnelStepData = {
      ...data,
      userId: data.userId || authStore.user?.id,
      organizationId: data.organizationId || authStore.user?.organization_id,
      sessionId: data.sessionId || this.sessionId,
      timestamp: new Date().toISOString(),
      device: this.getDeviceType(),
      source: "funnel_tracking",
    };

    // Utiliser le service Tianji existant qui utilise window.tianji.track
    await tianjiService.track("step_passed", enrichedData);

    // Console log removed for production
  }

  /**
   * Méthodes spécifiques pour chaque funnel
   */

  // 🎣 Funnel Réservation
  async trackReservationStep(
    step: string,
    lakeId?: string,
    extra: Record<string, unknown> = {},
  ): Promise<void> {
    await this.trackStep({
      funnel: "reservation",
      step,
      lakeId,
      ...extra,
    });
  }

  // 🚀 Funnel Onboarding
  async trackOnboardingStep(
    step: string,
    extra: Record<string, unknown> = {},
  ): Promise<void> {
    await this.trackStep({
      funnel: "onboarding",
      step,
      ...extra,
    });
  }

  // ⚡ Funnel Activation
  async trackActivationStep(
    step: string,
    featureName?: string,
    extra: Record<string, unknown> = {},
  ): Promise<void> {
    await this.trackStep({
      funnel: "activation",
      step,
      feature_name: featureName,
      ...extra,
    });
  }

  /**
   * Méthodes utilitaires
   */

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem("mylimni_session_id");
    if (!sessionId) {
      // Use crypto.getRandomValues for better security instead of Math.random()
      const randomBytes = new Uint8Array(5);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(randomBytes);
        sessionId =
          "sess-" +
          Array.from(randomBytes, byte => byte.toString(36)).join('').substring(0, 8) +
          "-" +
          Date.now();
      } else {
        // Fallback for environments without crypto API
        // Note: Math.random() is acceptable here as a fallback since crypto is unavailable
        sessionId =
          "sess-" +
          // eslint-disable-next-line sonarjs/pseudo-random
          Math.random().toString(36).substring(2, 10) +
          "-" +
          Date.now();
      }
      sessionStorage.setItem("mylimni_session_id", sessionId);
    }
    return sessionId;
  }

  private getDeviceType(): string {
    return /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";
  }

  /**
   * Obtenir la configuration d'un funnel
   */
  getFunnelConfig(funnelName: string): FunnelConfig | undefined {
    return FUNNEL_CONFIGS[funnelName];
  }

  /**
   * Lister tous les funnels configurés
   */
  getAllFunnels(): FunnelConfig[] {
    return Object.values(FUNNEL_CONFIGS);
  }

  /**
   * Vérifier si une étape existe dans un funnel
   */
  isValidStep(funnel: string, step: string): boolean {
    const config = FUNNEL_CONFIGS[funnel];
    return config ? config.steps.includes(step) : false;
  }

  /**
   * Tracker l'affichage du guide de configuration
   */
  async trackGuideShown(
    guideId: string,
    extraData: Record<string, unknown> = {},
  ): Promise<void> {
    return this.trackStep({
      funnel: "onboarding",
      step: "lake_configuration_guide_shown",
      guideId,
      ...extraData,
    });
  }

  /**
   * Tracker la complétion du guide de configuration
   */
  async trackGuideCompleted(
    guideId: string,
    action: "completed" | "skipped",
    extraData: Record<string, unknown> = {},
  ): Promise<void> {
    return this.trackStep({
      funnel: "onboarding",
      step: "lake_configuration_guide_completed",
      guideId,
      action,
      ...extraData,
    });
  }
}

// Instance globale exportée
export const funnelService = new FunnelService();

// Export par défaut pour plugin Vue
export default {
  install(app: App) {
    app.config.globalProperties.$funnel = funnelService;
    app.provide("funnel", funnelService);
  },
};
